/**
 * Sales Channels — the web data layer for the channel entity, the sales rung
 * up on it, and the batch payouts that settle them.
 *
 * Every call goes through `/api/v1/sales-channels` (apps/api/src/routes/
 * salesChannels.ts), which is mounted OUTSIDE the typed Hono chain to dodge
 * the RPC-type depth limit — hence `authedGet`/`authedPost`/`authedPatch`
 * rather than the `api.*` client. The route already turns a `{success:false}`
 * RPC envelope into a 400 carrying the RPC's own message, and `unwrap` turns
 * that into an `ApiError`, so a rejected sale reaches the toast with the
 * database's wording ("Sale is settled by payout POSP-000003 — void the payout
 * first") instead of a generic failure.
 *
 * ## Query keys
 *
 *   ['salesChannels', orgId, 'list']                — the card strip
 *   ['salesChannels', orgId, 'sales',   channelId]  — one channel's sales
 *   ['salesChannels', orgId, 'payouts', channelId]  — one channel's payouts
 *   ['salesChannels', orgId, 'saleAttachments', saleId] — one sale's files
 *   ['salesChannels', orgId, 'feed', channelId]     — a Stripe channel's Feed
 *   ['salesChannels', orgId, 'feedEvent', eventId]  — one Feed row's payload
 *   ['salesChannels', orgId, 'paymentLinks', channelId] — a Stripe channel's links
 *
 * The `['salesChannels', orgId]` PREFIX is the feature's liveness contract:
 * every write below invalidates it, which refreshes the tab bodies AND the
 * card strip's `unsettled_amount` — the number that moves on literally every
 * sale and payout, on cards for channels that are not even selected. Writes
 * nobody in the browser made — the Stripe sweep booking the Feed — arrive over
 * Realtime (`useSalesChannelFeedRealtime`) and take the same invalidation.
 *
 * ## …and why the AR keys are in here too
 *
 * A channel sale IS an AR invoice: `create_channel_sale` writes
 * `accounts_receivable` + `invoice_line_items` + a ledger journal, and a payout
 * posts N `process_ar_payment` receipts plus a settlement journal. So the sale
 * and payout mutations carry the SAME invalidation set every AR write in
 * `useAccountsReceivable.ts` carries (:233-265 there):
 *
 *   - `['accountsReceivable', orgId]` — the load-bearing prefix the Receivables
 *     index grid caches under. The domain has three names (`ar-invoices`
 *     routes, `*_ar_invoice_*` RPCs, `accountsReceivable` prefix) and only this
 *     one is wired to anything; a channel sale that skipped it would appear in
 *     the Sales tab and be invisible on /accounting/receivables until a reload.
 *   - `['ledgerTransactions', orgId]`, `['accountBalances', orgId]`,
 *     `['suspense-account-balance', orgId]`,
 *     `['accountTransactionsWithBalance', orgId]` — the journals and the
 *     per-account registers the posting touched (clearing, revenue, tax, AR,
 *     and for a payout the bank + fee accounts).
 *   - `['arInvoice', orgId, …]` — the lazy detail read (payment history,
 *     amount_received) behind an expanded AR row.
 *   - `['audit', orgId, …]` (`invalidateAuditCaches`) — every audit read: an
 *     open History disclosure, and Settings → Audit Log's list, activity chart
 *     and record search, which stay mounted while a sale or payout lands in
 *     another tab. Channel create / update invalidate it too (a channel and
 *     its clearing account are audited records).
 */
import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RealtimeChannel } from '@supabase/supabase-js';
import type {
  ConnectStripeChannelInput,
  ConnectStripeChannelResponse,
  CreateSalesChannelPaymentLinkInput,
  SalesChannelFeedEventDetailWire,
  SalesChannelPaymentLinkListResponse,
  SalesChannelPaymentLinkResponse,
  SalesChannelPaymentLinkRowWire,
  UpdateSalesChannelPaymentLinkInput,
  SalesChannelFeedEventRowWire,
  StripeChannelCallbackInput,
  StripeChannelCallbackResponse,
  StripeChannelDefaultsInput,
  StripeChannelDisconnectResponse,
  ChannelPayoutListResponse,
  ChannelPayoutRowWire,
  ChannelSaleIndexRowWire,
  ChannelSaleListResponse,
  CreateChannelPayoutInput,
  CreateChannelSaleInput,
  EditChannelSaleInput,
  SalesChannelListResponse,
  SalesChannelRowWire,
} from '@releve/shared';
import { useOrganization } from '@/contexts/OrganizationContext';
// eslint-disable-next-line no-restricted-imports -- Realtime only (useSalesChannelFeedRealtime): postgres_changes is browser → Supabase WebSocket by design (docs/ARCHITECTURE.md); every read and write here goes through the API
import { supabase } from '@/integrations/supabase/client';
import { invalidateAuditCaches } from '@/lib/auditCacheKeys';
import { invalidateCarbonCaches } from '@/lib/carbonCacheKeys';
import { authedGet, authedPatch, authedPost } from '@/lib/api';
import { addBreadcrumb, captureException } from '@/lib/sentry';
import { toast } from '@/hooks/useToast';
import { indexQueryOptions } from '@/hooks/lib/indexQueryOptions';
import i18n from '@/i18n';
import type { TransactionAttachment } from '@/hooks/useTransactionAttachments';

const BASE = '/api/v1/sales-channels';

/** Shared prefix — see the module header. */
export const salesChannelsKey = (organizationId: string) =>
  ['salesChannels', organizationId] as const;

/** Every RPC in this feature answers with the standard envelope. */
export interface SalesChannelMutationResult {
  success: boolean;
  error?: string;
  error_code?: string;
  [key: string]: unknown;
}

/** Stripe Disconnect — the RPC envelope plus what happened on Stripe's side. */
type StripeDisconnectResult = SalesChannelMutationResult &
  Pick<StripeChannelDisconnectResponse, 'stripe_uninstall'>;

/** `create_sales_channel` — the channel plus its auto-provisioned clearing account. */
export interface CreateSalesChannelResult extends SalesChannelMutationResult {
  sales_channel_id: string;
  clearing_account_id: string;
  name: string;
}

/** `create_channel_sale` — the AR invoice, its POS- number and its journal. */
export interface CreateChannelSaleResult extends SalesChannelMutationResult {
  ar_invoice_id: string;
  invoice_number: string;
  sales_channel_id: string;
  ledger_transaction_id: string;
}

/**
 * `edit_channel_sale` — the SAME sale (same id, same POS- number) with its new
 * posting journal, plus the void journal that reversed the old one.
 */
export interface EditChannelSaleResult extends SalesChannelMutationResult {
  ar_invoice_id: string;
  invoice_number: string;
  ledger_transaction_id: string;
  transaction_number: string;
  void_transaction_id: string;
  void_transaction_number: string;
  void_transaction_date: string;
  amount_total: number;
}

/** `create_channel_payout` — the payout, its POSP- number and the settlement journal. */
export interface CreateChannelPayoutResult extends SalesChannelMutationResult {
  payout_id: string;
  payout_number: string;
  /** The ONE bank-touching settlement journal — where a payout's files hang. */
  settlement_transaction_id: string;
  gross_amount: number;
  fee_amount: number;
}

/**
 * The API returns 200 only on a `success: true` envelope (a false one is
 * already a 400 → `ApiError`), so this guard should never fire. It exists so a
 * future route that forwards the envelope with a 200 cannot silently report a
 * refused write as a success.
 */
function assertOk<T extends SalesChannelMutationResult>(result: T, fallback: string): T {
  if (!result?.success) throw new Error(result?.error || fallback);
  return result;
}

/**
 * Invalidate everything a channel sale or payout moved: the feature's own
 * queries plus the AR write set (see the module header for why each is here).
 * `invoiceIds` is the AR detail read to refresh — a create passes none (the id
 * is brand new, so there is no cached detail), a void or an edit passes the
 * one sale, and a payout passes the whole allocation set.
 */
function invalidateChannelWrite(
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string,
  invoiceIds: string[] = [],
) {
  // The feature: card strip (unsettled_amount) + sales tab + payouts tab.
  void queryClient.invalidateQueries({ queryKey: salesChannelsKey(organizationId) });

  // The AR write set — useAccountsReceivable.ts:233-265.
  void queryClient.invalidateQueries({ queryKey: ['accountsReceivable', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['accountBalances', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
  void queryClient.invalidateQueries({
    queryKey: ['accountTransactionsWithBalance', organizationId],
  });
  for (const invoiceId of invoiceIds) {
    void queryClient.invalidateQueries({ queryKey: ['arInvoice', organizationId, invoiceId] });
  }
  // The bank feed. A payout posts the ONE settlement journal this whole feature
  // exists to match against a deposit line, so the feed index and its
  // match-hint badges (60s staleTime) are stale the moment it lands — every
  // sibling that posts a matchable journal invalidates this key too
  // (useAddExpenseFromImport, useAccountsPayable, useLinkToAccountingImport,
  // useClearingActions). A PLAIN invalidation, deliberately not
  // useClearingActions' `refetchType: 'none'` predicate: that trick exists
  // because a clearing COMMIT changes the pending set, which re-keys the
  // lookaheads onto a fresh cache entry that fetches by itself. A channel write
  // changes no feed row and so re-keys nothing — only a real refetch refreshes
  // the badges.
  void queryClient.invalidateQueries({ queryKey: ['accountingImports', organizationId] });
  // The audit trail: History disclosures + the Audit Log page (list, chart, search).
  invalidateAuditCaches(queryClient, organizationId);
  // Carbon totals read the live ledger: a void changes which journals count,
  // so the Impact register and every carbon total refresh. Defensive on the
  // other channel writes (their journals carry no carbon today); mounted
  // queries only, so cheap.
  invalidateCarbonCaches(queryClient, organizationId);
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * The org's sales channels — the card strip's whole data source. Each row
 * carries its clearing account, its `unsettled_amount` (a server-side lateral
 * sum: totalling a channel's sales in the browser would be the
 * unbounded-client-side-aggregation anti-pattern) and the bank/fee accounts of
 * its most recent payout, which seed the payout dialog's pickers.
 *
 * Reference data by the CLAUDE.md rule — a channel roster changes when someone
 * adds a channel, which invalidates the key anyway. `unsettled_amount` rides
 * along on the same row, and every write that moves it invalidates this key
 * through `invalidateChannelWrite`, so the staleTime never hides a stale
 * balance behind a fresh number.
 */
export const useSalesChannels = () => {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useQuery({
    queryKey: [...salesChannelsKey(organizationId ?? ''), 'list'],
    queryFn: async (): Promise<SalesChannelRowWire[]> => {
      if (!organizationId) return [];
      const result = await authedGet<SalesChannelListResponse>(
        `${BASE}/orgs/${organizationId}/channels`,
      );
      return result.items ?? [];
    },
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
};

/**
 * One channel's sales, newest-first, as `ar_invoice_index` rows (the endpoint
 * reads the very view the Receivables grid reads, so the two can never drift).
 * The API pages it past PostgREST's silent 1,000-row cap, so this list is
 * complete — the Unsettled/All toggle filters it client-side without a
 * refetch.
 *
 * No `staleTime`: a box office rings sales up continuously and this is the tab
 * the operator is looking at.
 */
export const useChannelSales = (channelId?: string) => {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useQuery({
    queryKey: [...salesChannelsKey(organizationId ?? ''), 'sales', channelId ?? null],
    queryFn: async (): Promise<ChannelSaleIndexRowWire[]> => {
      if (!organizationId || !channelId) return [];
      const result = await authedGet<ChannelSaleListResponse>(
        `${BASE}/orgs/${organizationId}/sales?channelId=${encodeURIComponent(channelId)}`,
      );
      return result.items ?? [];
    },
    enabled: !!organizationId && !!channelId,
  });
};

/**
 * One channel's payouts, newest-first, each with its allocations embedded (the
 * expanded row needs them the moment it opens, and row 0 seeds the payout
 * dialog's bank/fee defaults on a channel whose card-strip row has not been
 * refetched yet).
 */
export const useChannelPayouts = (channelId?: string) => {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useQuery({
    queryKey: [...salesChannelsKey(organizationId ?? ''), 'payouts', channelId ?? null],
    queryFn: async (): Promise<ChannelPayoutRowWire[]> => {
      if (!organizationId || !channelId) return [];
      const result = await authedGet<ChannelPayoutListResponse>(
        `${BASE}/orgs/${organizationId}/payouts?channelId=${encodeURIComponent(channelId)}`,
      );
      return result.items ?? [];
    },
    enabled: !!organizationId && !!channelId,
  });
};

/**
 * One sale's attachments, read across EVERY journal tagged with the sale
 * (`ledger_transactions.ar_invoice_id`). An edit re-points the sale at a new
 * posting and `transaction_attachments` links cannot be moved, so reading only
 * the current posting would hide every file added before the last edit — see
 * the route in apps/api/src/routes/accounting/salesChannels.ts.
 *
 * Under the feature prefix, so `invalidateChannelWrite` refreshes it after an
 * edit; the attachment upload/delete hooks invalidate the `'saleAttachments'`
 * sub-key themselves.
 */
export const useChannelSaleAttachments = (saleId?: string | null) => {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useQuery({
    queryKey: [...salesChannelsKey(organizationId ?? ''), 'saleAttachments', saleId ?? null],
    queryFn: async (): Promise<TransactionAttachment[]> => {
      if (!organizationId || !saleId) return [];
      const result = await authedGet<{ items: TransactionAttachment[] }>(
        `${BASE}/orgs/${organizationId}/sales/${encodeURIComponent(saleId)}/attachments`,
      );
      return result.items ?? [];
    },
    enabled: !!organizationId && !!saleId,
  });
};

// ---------------------------------------------------------------------------
// Channel entity writes
// ---------------------------------------------------------------------------

/**
 * Create a channel. The RPC also provisions its dedicated `channel_clearing`
 * chart-of-accounts row in the same statement, which is why the CoA cache is
 * invalidated alongside the channel list — the new account is immediately
 * visible in account pickers and on the balance sheet.
 */
export const useCreateSalesChannel = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationKey: ['salesChannels', 'createChannel'],
    mutationFn: async ({ name }: { name: string }) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      const result = await authedPost<CreateSalesChannelResult>(
        `${BASE}/orgs/${organizationId}/channels`,
        { name },
      );
      return assertOk(result, 'Failed to create the sales channel');
    },
    onSuccess: (result) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) return;
      void queryClient.invalidateQueries({ queryKey: salesChannelsKey(organizationId) });
      // The clearing account the RPC just created.
      void queryClient.invalidateQueries({ queryKey: ['chartOfAccounts', organizationId] });
      invalidateAuditCaches(queryClient, organizationId);
      toast.success(`Sales channel "${result.name}" created`);
    },
    onError: (error) => {
      captureException(error, { feature: 'salesChannels.createChannel' });
      toast.error(error.message);
    },
  });
};

/**
 * Rename and/or (de)activate a channel. There is no delete — `is_active:false`
 * is the only retirement path, and the table carries no DELETE RLS policy at
 * all. A rename does NOT rename the clearing account (accepted: the account
 * keeps its creation name so its ledger history stays legible).
 */
export const useUpdateSalesChannel = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationKey: ['salesChannels', 'updateChannel'],
    mutationFn: async ({
      channelId,
      name,
      is_active,
      payout_bank_account_id,
      fee_account_id,
      defaults,
    }: {
      channelId: string;
      name?: string;
      is_active?: boolean;
      /** kind = 'stripe' only — omitted = leave alone (never cleared). */
      payout_bank_account_id?: string;
      fee_account_id?: string;
      /**
       * kind = 'stripe' only — the "other Stripe payments" booking rule,
       * written AS A GROUP (a null member clears that default).
       */
      defaults?: StripeChannelDefaultsInput;
    }) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      // Omit rather than send null: the RPC reads an absent argument as
      // "leave this field alone", and the schema is `.strict()`.
      const body: {
        name?: string;
        is_active?: boolean;
        payout_bank_account_id?: string;
        fee_account_id?: string;
        defaults?: StripeChannelDefaultsInput;
      } = {};
      if (name !== undefined) body.name = name;
      if (is_active !== undefined) body.is_active = is_active;
      if (payout_bank_account_id !== undefined) body.payout_bank_account_id = payout_bank_account_id;
      if (fee_account_id !== undefined) body.fee_account_id = fee_account_id;
      if (defaults !== undefined) body.defaults = defaults;
      const result = await authedPatch<SalesChannelMutationResult>(
        `${BASE}/orgs/${organizationId}/channels/${channelId}`,
        body,
      );
      return assertOk(result, 'Failed to update the sales channel');
    },
    onSuccess: () => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) return;
      void queryClient.invalidateQueries({ queryKey: salesChannelsKey(organizationId) });
      invalidateAuditCaches(queryClient, organizationId);
      toast.success('Sales channel updated');
    },
    onError: (error) => {
      captureException(error, { feature: 'salesChannels.updateChannel' });
      toast.error(error.message);
    },
  });
};

// ---------------------------------------------------------------------------
// Sale writes
// ---------------------------------------------------------------------------

/**
 * Ring up one sale. Posts the whole document — AR row, lines, journal — in a
 * single RPC, so there is no partial state to clean up if it fails.
 *
 * No detail key is invalidated: the invoice id is brand new, so nothing is
 * cached under it (AR-create parity).
 */
export const useCreateChannelSale = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationKey: ['salesChannels', 'createSale'],
    mutationFn: async (data: CreateChannelSaleInput) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      addBreadcrumb('salesChannels.sale.create: started', {
        organizationId,
        salesChannelId: data.sales_channel_id,
        lineCount: data.line_items.length,
      });
      const result = await authedPost<CreateChannelSaleResult>(
        `${BASE}/orgs/${organizationId}/sales`,
        data,
      );
      return assertOk(result, 'Failed to record the sale');
    },
    onSuccess: (result) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) return;
      invalidateChannelWrite(queryClient, organizationId);
      toast.success(`Sale ${result.invoice_number} recorded`);
    },
    onError: (error) => {
      captureException(error, { feature: 'salesChannels.createSale' });
      toast.error(error.message);
    },
  });
};

/**
 * Edit a sale — reverse and re-post, never an in-place rewrite. The RPC posts a
 * void journal against the current posting and a fresh posting for the
 * corrected sale, under the SAME POS- number and `accounts_receivable` row
 * (docs/SALES_CHANNELS.md). It refuses a voided sale (`ALREADY_VOIDED`) and one
 * a payout has touched (`HAS_PAYOUT` — void the payout first); either refusal
 * arrives here as the error message the toast shows.
 */
export const useEditChannelSale = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationKey: ['salesChannels', 'editSale'],
    mutationFn: async ({ invoiceId, data }: { invoiceId: string; data: EditChannelSaleInput }) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      addBreadcrumb('salesChannels.sale.edit: started', {
        organizationId,
        invoiceId,
        lineCount: data.line_items.length,
      });
      const result = await authedPatch<EditChannelSaleResult>(
        `${BASE}/orgs/${organizationId}/sales/${invoiceId}`,
        data,
      );
      return assertOk(result, 'Failed to update the sale');
    },
    onSuccess: (result, { invoiceId }) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) return;
      // The detail read holds the lines, the header and the posting journal the
      // edit just replaced.
      invalidateChannelWrite(queryClient, organizationId, [invoiceId]);
      toast.success(`Sale ${result.invoice_number} updated`);
    },
    onError: (error) => {
      captureException(error, { feature: 'salesChannels.editSale' });
      toast.error(error.message);
    },
  });
};

/**
 * Void a sale. A void is a REVERSING journal, never a delete: the sale keeps
 * its POS- number and its original posting forever, so the receipt series
 * stays unbroken (docs/AUDIT.md §7.3). The RPC refuses a sale that a live
 * payout already settled (`HAS_PAYOUT` — void the payout first), which arrives
 * here as the error message the toast shows.
 */
export const useVoidChannelSale = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationKey: ['salesChannels', 'voidSale'],
    mutationFn: async ({ invoiceId, reason }: { invoiceId: string; reason?: string }) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      addBreadcrumb('salesChannels.sale.void: started', { organizationId, invoiceId });
      const result = await authedPost<SalesChannelMutationResult>(
        `${BASE}/orgs/${organizationId}/sales/${invoiceId}/void`,
        reason ? { reason } : {},
      );
      return assertOk(result, 'Failed to void the sale');
    },
    onSuccess: (_result, { invoiceId }) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) return;
      // The detail read holds the status and the payment history the reversing
      // journal just changed.
      invalidateChannelWrite(queryClient, organizationId, [invoiceId]);
      toast.success('Sale voided');
    },
    onError: (error) => {
      captureException(error, { feature: 'salesChannels.voidSale' });
      toast.error(error.message);
    },
  });
};

// ---------------------------------------------------------------------------
// Payout writes
// ---------------------------------------------------------------------------

/**
 * Record one batch payout: N `DR clearing / CR AR` receipts plus the ONE
 * bank-touching settlement journal (`DR bank net + DR fee / CR clearing
 * gross`) whose amount equals the processor's deposit, so it matches 1:1 in
 * the bank feed. Every allocated sale's detail cache is refreshed — each one
 * just gained a payment and (usually) flipped to 'paid'.
 */
export const useCreateChannelPayout = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationKey: ['salesChannels', 'createPayout'],
    mutationFn: async (data: CreateChannelPayoutInput) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      addBreadcrumb('salesChannels.payout.create: started', {
        organizationId,
        salesChannelId: data.sales_channel_id,
        allocationCount: data.allocations.length,
      });
      const result = await authedPost<CreateChannelPayoutResult>(
        `${BASE}/orgs/${organizationId}/payouts`,
        data,
      );
      return assertOk(result, 'Failed to record the payout');
    },
    onSuccess: (result, variables) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) return;
      invalidateChannelWrite(
        queryClient,
        organizationId,
        variables.allocations.map((allocation) => allocation.ar_invoice_id),
      );
      toast.success(`Payout ${result.payout_number} recorded`);
    },
    onError: (error) => {
      captureException(error, { feature: 'salesChannels.createPayout' });
      toast.error(error.message);
    },
  });
};

/**
 * Void a payout — a reversing journal for every receipt AND for the
 * settlement, never a delete. The settled sales revert to 'sent' through the
 * ledger sync trigger, which is what makes them voidable again, so their
 * detail caches must be refreshed alongside the payout list. The RPC refuses a
 * payout whose journals are matched to a bank import (`IMPORT_LINKED` — undo
 * the match first).
 *
 * `allocatedInvoiceIds` comes from the payout row the caller already has
 * (`ChannelPayoutRowWire.allocations`); pass it so the reverted sales' detail
 * reads are refreshed too.
 */
export const useVoidChannelPayout = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationKey: ['salesChannels', 'voidPayout'],
    mutationFn: async ({
      payoutId,
      reason,
    }: {
      payoutId: string;
      reason?: string;
      allocatedInvoiceIds?: string[];
    }) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      addBreadcrumb('salesChannels.payout.void: started', { organizationId, payoutId });
      const result = await authedPost<SalesChannelMutationResult>(
        `${BASE}/orgs/${organizationId}/payouts/${payoutId}/void`,
        reason ? { reason } : {},
      );
      return assertOk(result, 'Failed to void the payout');
    },
    onSuccess: (_result, { allocatedInvoiceIds }) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) return;
      invalidateChannelWrite(queryClient, organizationId, allocatedInvoiceIds ?? []);
      toast.success('Payout voided');
    },
    onError: (error) => {
      captureException(error, { feature: 'salesChannels.voidPayout' });
      toast.error(error.message);
    },
  });
};


// ---------------------------------------------------------------------------
// kind = 'stripe' — Connect, Disconnect, Resume, and the Feed
// ---------------------------------------------------------------------------
//
// Connect = installing the public Relevé Stripe App through its install link
// (apps/stripe-app/README.md); Disconnect = uninstalling it through Stripe's
// Install API. No OAuth, no tokens: the API keeps only the `acct_…` id.
//
// Errors: these hooks TOAST ONLY. The app-level `MutationCache.onError`
// already files every mutation failure with Sentry (docs/OBSERVABILITY.md); a
// second `captureException` here would double-report. The callback's coded
// refusals (a duplicate name, an account already connected, an expired state)
// are user outcomes the `/stripe-callback` page renders, so they are declared
// expected and never reach Sentry at all.

/** Codes `POST …/channels/stripe/callback` answers with that are user outcomes, not faults. */
export const EXPECTED_STRIPE_CALLBACK_ERROR_CODES = [
  'DUPLICATE_NAME',
  'EXTERNAL_ACCOUNT_IN_USE',
  'STRIPE_STATE_EXPIRED',
  'STRIPE_STATE_INVALID',
  'STRIPE_INSTALL_SIGNATURE_INVALID',
  'MODE_MISMATCH',
] as const;

/**
 * Start the install of the Relevé Stripe App. Writes nothing: the channel
 * config travels in the signed state and the CALLBACK creates the channel, so
 * an abandoned install leaves no orphan channel or clearing account. The
 * caller does the full-page redirect to the returned `url` — the app's install
 * link carrying our `state` and `redirect_uri` (the SPA unloads there).
 *
 * `organizationId` is explicit (not read from context) because the org-less
 * `/stripe-callback` page re-runs this for its rename-and-retry, where the org
 * comes from the decoded state.
 */
export const useStartStripeConnection = (organizationId?: string) =>
  useMutation({
    mutationKey: ['salesChannels', 'stripeConnect'],
    meta: { expectedErrorCodes: ['DUPLICATE_NAME', 'FOREIGN_REFERENCE'] },
    mutationFn: async (input: ConnectStripeChannelInput) => {
      if (!organizationId) throw new Error('No organization selected');
      addBreadcrumb('salesChannels.stripe.connect: started', { organizationId });
      return authedPost<ConnectStripeChannelResponse>(
        `${BASE}/orgs/${organizationId}/channels/stripe/connect`,
        input,
      );
    },
    onError: (error) => {
      toast.error(error.message || i18n.t('salesChannels.stripe.toast.connectFailed'));
    },
  });

/**
 * Complete the install: the page forwards what Stripe's redirect carried
 * (`state`, `user_id`, `account_id`, `install_signature`, `livemode`), and the
 * server verifies our state (org, user, expiry), Stripe's install signature
 * and the install's mode, then creates — or reconnects — the channel. The org
 * comes from the decoded state, not from context: `/stripe-callback` renders
 * outside `OrgLayout`.
 *
 * No toast: the callback page renders every outcome itself.
 */
export const useCompleteStripeConnection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['salesChannels', 'stripeCallback'],
    meta: { expectedErrorCodes: [...EXPECTED_STRIPE_CALLBACK_ERROR_CODES] },
    mutationFn: async ({
      organizationId,
      ...body
    }: StripeChannelCallbackInput & { organizationId: string }) => {
      addBreadcrumb('salesChannels.stripe.callback: started', { organizationId });
      return authedPost<StripeChannelCallbackResponse>(
        `${BASE}/orgs/${organizationId}/channels/stripe/callback`,
        body,
      );
    },
    onSuccess: (_result, { organizationId }) => {
      void queryClient.invalidateQueries({ queryKey: salesChannelsKey(organizationId) });
      // The clearing account a brand-new channel provisions.
      void queryClient.invalidateQueries({ queryKey: ['chartOfAccounts', organizationId] });
      invalidateAuditCaches(queryClient, organizationId);
    },
  });
};

/**
 * Disconnect a Stripe channel — Stripe first (the Install API uninstalls the
 * Relevé app from the account), then the row goes `disconnected`, in every
 * case. When Stripe could not be reached (`stripe_uninstall: 'failed'`) the
 * app may still be installed, so the toast is a warning that tells the user to
 * uninstall it in Stripe. The channel keeps its history; installing the app on
 * the same Stripe account again reconnects THIS channel.
 */
export const useDisconnectStripeChannel = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationKey: ['salesChannels', 'stripeDisconnect'],
    mutationFn: async ({ channelId }: { channelId: string }) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      addBreadcrumb('salesChannels.stripe.disconnect: started', { organizationId, channelId });
      const result = await authedPost<StripeDisconnectResult>(
        `${BASE}/orgs/${organizationId}/channels/${channelId}/stripe/disconnect`,
        {},
      );
      return assertOk(result, 'Failed to disconnect the Stripe account');
    },
    onSuccess: (result) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) return;
      void queryClient.invalidateQueries({ queryKey: salesChannelsKey(organizationId) });
      invalidateAuditCaches(queryClient, organizationId);
      if (result.stripe_uninstall === 'failed') {
        // It asks the user to act in Stripe, so it stays until dismissed.
        toast.warning(i18n.t('salesChannels.stripe.toast.disconnectedUninstallFailed'), {
          duration: Infinity,
          closeButton: true,
        });
      } else {
        toast.success(i18n.t('salesChannels.stripe.toast.disconnected'));
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

/**
 * Resume a stopped Feed (`external_status` error → connected). The CALLER
 * becomes the user the booking sweep acts as — how a new person takes over a
 * feed whose connector left or lost the accounting role.
 */
export const useResumeChannelFeed = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationKey: ['salesChannels', 'feedResume'],
    mutationFn: async ({ channelId }: { channelId: string }) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      addBreadcrumb('salesChannels.feed.resume: started', { organizationId, channelId });
      const result = await authedPost<SalesChannelMutationResult>(
        `${BASE}/orgs/${organizationId}/channels/${channelId}/feed/resume`,
        {},
      );
      return assertOk(result, 'Failed to resume the feed');
    },
    onSuccess: () => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) return;
      void queryClient.invalidateQueries({ queryKey: salesChannelsKey(organizationId) });
      invalidateAuditCaches(queryClient, organizationId);
      toast.success(i18n.t('salesChannels.stripe.toast.resumed'));
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

/** While any row is `pending` the sweep (every minute) is about to book it. */
const FEED_PENDING_REFETCH_MS = 30_000;

/**
 * One Stripe channel's Feed — the webhook inbox the pg_cron sweep books. The
 * COMPLETE list (the API keyset-pages it past PostgREST's 1,000-row cap), on
 * the shared slim-index options builder, keyed under the feature prefix:
 * `['salesChannels', orgId, 'feed', channelId]` rather than the builder's
 * `[…, 'index', params]`, so the key names the channel and every channel
 * write's `salesChannelsKey` invalidation refreshes it.
 *
 * Polls every 30s while a row is `pending` — booking lags the payment by about
 * a minute, and a Retry puts a row back in that state — and stops once the
 * queue is settled.
 */
export const useChannelFeedEvents = (channelId?: string) => {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  const options = indexQueryOptions<SalesChannelFeedEventRowWire>({
    entity: 'salesChannels',
    organizationId: channelId ? organizationId : undefined,
    path:
      organizationId && channelId
        ? `${BASE}/orgs/${organizationId}/channels/${encodeURIComponent(channelId)}/feed-events`
        : '',
    staleTime: 30_000,
  });

  return useQuery({
    ...options,
    queryKey: [...salesChannelsKey(organizationId ?? ''), 'feed', channelId ?? null],
    // Never carry one channel's rows over as another's placeholder.
    placeholderData: undefined,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((row) => row.status === 'pending')
        ? FEED_PENDING_REFETCH_MS
        : false,
  });
};

/**
 * One Feed row with its whitelisted payload — the expanded row's lazy read
 * (the slim list leaves the payload out: a payout's carries every balance
 * transaction).
 */
export const useChannelFeedEvent = (eventId?: string | null) => {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useQuery({
    queryKey: [...salesChannelsKey(organizationId ?? ''), 'feedEvent', eventId ?? null],
    queryFn: async (): Promise<SalesChannelFeedEventDetailWire | null> => {
      if (!organizationId || !eventId) return null;
      return authedGet<SalesChannelFeedEventDetailWire>(
        `${BASE}/orgs/${organizationId}/feed-events/${encodeURIComponent(eventId)}`,
      );
    },
    enabled: !!organizationId && !!eventId,
  });
};

export type FeedEventAction = 'retry' | 'dismiss';

/**
 * Retry (needs_attention → pending; the sweep books it again within a minute)
 * or Dismiss (→ dismissed, terminal: "handled by hand") one Feed row. Refreshes
 * the feature prefix — the Feed itself and the card strip's attention count.
 */
export const useSetFeedEventStatus = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationKey: ['salesChannels', 'feedEventStatus'],
    mutationFn: async ({ eventId, action }: { eventId: string; action: FeedEventAction }) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      addBreadcrumb('salesChannels.feed.status: started', { organizationId, eventId, action });
      const result = await authedPost<SalesChannelMutationResult>(
        `${BASE}/orgs/${organizationId}/feed-events/${encodeURIComponent(eventId)}/status`,
        { action },
      );
      return assertOk(result, 'Failed to update the feed event');
    },
    onSuccess: (_result, { action }) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) return;
      void queryClient.invalidateQueries({ queryKey: salesChannelsKey(organizationId) });
      toast.success(
        i18n.t(
          action === 'retry'
            ? 'salesChannels.feed.toast.retried'
            : 'salesChannels.feed.toast.dismissed',
        ),
      );
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// ---------------------------------------------------------------------------
// Stripe payment links (Layer 2) — GET / POST …/channels/:channelId/payment-links,
// PATCH …/payment-links/:linkId. The list reads the table (never Stripe).
// ---------------------------------------------------------------------------

/**
 * One Stripe channel's payment links, newest first, active and deactivated.
 * Keyed under the feature prefix so every channel write's `salesChannelsKey`
 * invalidation refreshes it too. Links change only when someone edits them
 * here, so a short staleTime is enough.
 */
export const useChannelPaymentLinks = (channelId?: string) => {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useQuery({
    queryKey: [...salesChannelsKey(organizationId ?? ''), 'paymentLinks', channelId ?? null],
    queryFn: async (): Promise<SalesChannelPaymentLinkRowWire[]> => {
      if (!organizationId || !channelId) return [];
      const result = await authedGet<SalesChannelPaymentLinkListResponse>(
        `${BASE}/orgs/${organizationId}/channels/${encodeURIComponent(channelId)}/payment-links`,
      );
      return result.items ?? [];
    },
    enabled: !!organizationId && !!channelId,
    staleTime: 60_000,
  });
};

/** A payment-link write refreshes the feature prefix (the Links tab) and the audit reads. */
function invalidatePaymentLinkWrite(
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string,
) {
  void queryClient.invalidateQueries({ queryKey: salesChannelsKey(organizationId) });
  invalidateAuditCaches(queryClient, organizationId);
}

/**
 * Create a payment link — the API mints the price and the link at Stripe,
 * then stores the row with its booking settings. Always the org currency.
 */
export const useCreateChannelPaymentLink = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationKey: ['salesChannels', 'createPaymentLink'],
    mutationFn: async ({
      channelId,
      input,
    }: {
      channelId: string;
      input: CreateSalesChannelPaymentLinkInput;
    }) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      addBreadcrumb('salesChannels.paymentLinks.create: started', { organizationId, channelId });
      const result = await authedPost<SalesChannelPaymentLinkResponse>(
        `${BASE}/orgs/${organizationId}/channels/${encodeURIComponent(channelId)}/payment-links`,
        input,
      );
      return result.payment_link;
    },
    onSuccess: () => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) return;
      invalidatePaymentLinkWrite(queryClient, organizationId);
      toast.success(i18n.t('salesChannels.links.toast.created'));
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

/**
 * Edit a link's label and/or booking settings, or deactivate it
 * (`is_active: false` — Stripe first, then the row; never reactivated).
 * Amounts and purpose are immutable: the schema refuses them.
 */
export const useUpdateChannelPaymentLink = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationKey: ['salesChannels', 'updatePaymentLink'],
    mutationFn: async ({
      channelId,
      linkId,
      input,
    }: {
      channelId: string;
      linkId: string;
      input: UpdateSalesChannelPaymentLinkInput;
    }) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      addBreadcrumb('salesChannels.paymentLinks.update: started', {
        organizationId,
        channelId,
        linkId,
        deactivate: input.is_active === false,
      });
      const result = await authedPatch<SalesChannelPaymentLinkResponse>(
        `${BASE}/orgs/${organizationId}/channels/${encodeURIComponent(channelId)}/payment-links/${encodeURIComponent(linkId)}`,
        input,
      );
      return result.payment_link;
    },
    onSuccess: (_link, { input }) => {
      const organizationId = selectedOrganization?.id;
      if (!organizationId) return;
      invalidatePaymentLinkWrite(queryClient, organizationId);
      toast.success(
        i18n.t(
          input.is_active === false
            ? 'salesChannels.links.toast.deactivated'
            : 'salesChannels.links.toast.updated',
        ),
      );
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

/** Coalesce a burst of Feed events (one sweep books many rows) into one refetch. */
const FEED_REALTIME_DEBOUNCE_MS = 500;

/**
 * Live refresh for the Stripe kind. The pg_cron sweep books the Feed
 * (`sales_channel_external_events`: pending → processed / needs_attention) with
 * no browser involved, so the Sales / Payouts / Feed tabs, the card strip and
 * the sales chart would stay stale until a reload. One channel per org listens
 * for INSERT + UPDATE on the inbox (RLS — the accounting-scope SELECT policy —
 * decides who receives them) and runs the ordinary `invalidateChannelWrite`.
 *
 * INSERT + UPDATE by name, never `*`: a DELETE event carries only the PK and is
 * neither RLS- nor filter-matched (docs/CHANNELS.md "Realtime"). Channel reuse
 * mirrors `useComments`: a `joined`/`joining` channel of the same name has a
 * live owner; any other leftover is removed before building.
 *
 * The Feed tab's 30s pending-poll stays as the fallback when Realtime is down;
 * with Realtime up the invalidation lands first and the poll has nothing new.
 */
export const useSalesChannelFeedRealtime = (organizationId: string | undefined, enabled: boolean) => {
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!enabled || !organizationId || channelRef.current) return;

    const channelName = `sales-channel-feed:${organizationId}`;
    const existingChannel = supabase.getChannels().find((ch) => ch.topic === `realtime:${channelName}`);
    if (existingChannel && (existingChannel.state === 'joined' || existingChannel.state === 'joining')) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const invalidate = () => {
      clearTimeout(timer);
      timer = setTimeout(() => invalidateChannelWrite(queryClient, organizationId), FEED_REALTIME_DEBOUNCE_MS);
    };
    const scope = {
      schema: 'public',
      table: 'sales_channel_external_events',
      filter: `organization_id=eq.${organizationId}`,
    } as const;

    const build = () => {
      channelRef.current = supabase
        .channel(channelName)
        .on('postgres_changes', { event: 'INSERT', ...scope }, invalidate)
        .on('postgres_changes', { event: 'UPDATE', ...scope }, invalidate)
        .subscribe((status, err) => {
          if (!err) return;
          // Transient transport drops reconnect by themselves — breadcrumbs,
          // not Sentry events (same split as useComments).
          if (/^(socket closed: \d+|heartbeat timeout|channel error: (transport failure|connection lost))/.test(err.message)) {
            addBreadcrumb(err.message, { status }, 'warning');
            return;
          }
          captureException(err instanceof Error ? err : new Error(String(err)), {
            context: 'Sales Channel Feed Realtime Subscription',
            status,
          });
        });
    };

    if (existingChannel) {
      void supabase.removeChannel(existingChannel).then(() => {
        if (!cancelled) build();
      });
    } else {
      build();
    }

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (channelRef.current) {
        void supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [enabled, organizationId, queryClient]);
};
